import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Formik, Form, FormikHelpers } from "formik";
import { useCreateDivision } from "../../data/useCreateDivision";
import { useUpdateDivision } from "../../data/useUpdateDivision";
import { useCreateTeamsBulk } from "../../data/useCreateTeamsBulk";
import { useTeams } from "../../data/useTeams";
import { useDeleteTeam } from "../../data/useDeleteTeam";
import { useUsers } from "../../data/useUsers";
import { useUpdateUserDetails } from "../../data/useUpdateUserDetails";
import { queryClient } from "../../queryClient";
import { applyServerFieldErrors, showError } from "../../utils/error-handling";
import { notify } from "../../utils/notify";
import { Box } from "../Box";
import { Button } from "../Button";
import { Image } from "../Image";
import { Input } from "../Input";
import { Loader } from "../Loader";
import { Paragraph } from "../Paragraph";
import { Slider } from "../Slider";
import { Toggle } from "../Toggle";
import { Flex } from "../Flex";
import { ProfileOrb } from "../ProfileOrb";
import { Link } from "../Link";
import CustomTabs from "../CustomTabs";
import { ConfirmModal } from "./ConfirmModal";
import deleteIcon from "../../assets/settings/delete-red.svg";
import { Division } from "../../types/Division";
import { Team } from "../../types/Team";
import { User } from "../../types/User";
import { ROUTE } from "../../routes";
import { useAuth } from "../../context/AuthContext";
import { teamDeleteBlockedReason } from "../../utils/teamDeletion";
import {
  addDivisionValidation,
  teamDraftValidation,
} from "../../utils/validationSchema";

type AddEditDivisionModalProps = {
  isOpen: boolean;
  onRequestClose: () => void;
  division?: Division | null;
};

type DivisionDraft = {
  name: string;
  display_name: string;
  code: string;
  is_active: boolean;
};

type TeamDraft = {
  name: string;
  display_name: string;
  is_active: boolean;
};

const DetailsTab = ({
  division,
  onSaved,
  onNext,
}: {
  division?: Division | null;
  onSaved: () => void;
  onNext: (draft: DivisionDraft) => void;
}) => {
  const mode = division ? "edit" : "create";
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const { mutate: updateDivision, isLoading: isLoadingUpdate } =
    useUpdateDivision();

  const handleSubmit = (values: any, { setErrors }: FormikHelpers<any>) => {
    const payload: DivisionDraft = {
      name: values.name.trim(),
      display_name: values.display_name.trim(),
      code: values.code.trim(),
      is_active: mode === "create" ? true : values.is_active,
    };

    if (mode === "create") {
      onNext(payload);
      return;
    }

    updateDivision(
      { id: division!.id, ...payload },
      {
        onSuccess: () => {
          notify("Division updated", { type: "success" });
          queryClient.invalidateQueries(["divisions"]);
          onSaved();
        },
        onError: (err: any) =>
          applyServerFieldErrors(
            err,
            ["name", "display_name", "code"],
            setErrors,
            "A problem occurred while updating the Division."
          ),
      }
    );
  };

  return (
    <Formik
      initialValues={{
        name: division?.name ?? "",
        display_name: division?.display_name ?? "",
        code: division?.code ?? "",
        is_active: division?.is_active ?? true,
      }}
      validationSchema={addDivisionValidation}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {(formikProps) => (
        <Form onSubmit={formikProps.handleSubmit}>
          <Input
            onChange={formikProps.handleChange}
            value={formikProps.values.name}
            placeholder="Enter division name"
            mb={3}
            label="Name"
            name="name"
            isRequired
            hasError={Boolean(formikProps.errors.name)}
            errorMessage={formikProps.errors.name as string}
          />
          <Input
            onChange={formikProps.handleChange}
            value={formikProps.values.display_name}
            placeholder="Enter display name"
            mb={3}
            label="Display name"
            name="display_name"
            isRequired
            hasError={Boolean(formikProps.errors.display_name)}
            errorMessage={formikProps.errors.display_name as string}
          />
          <Input
            onChange={formikProps.handleChange}
            value={formikProps.values.code}
            placeholder="e.g. JE"
            mb={3}
            label="Code"
            name="code"
            isRequired
            hasError={Boolean(formikProps.errors.code)}
            errorMessage={formikProps.errors.code as string}
          />

          {mode === "edit" && (
            <Flex alignItems="center" gap="8" mb={3}>
              <Paragraph m="0px">Active</Paragraph>
              <Toggle
                isChecked={formikProps.values.is_active}
                onChange={(e) =>
                  formikProps.setFieldValue("is_active", e.target.checked)
                }
              />
            </Flex>
          )}

          {mode === "edit" && (
            <Paragraph color="gray.50" fontSize={1} mb={3}>
              This screen only covers the Division itself. To configure what
              this Division shows applicants — application types, forms, or
              branding — go to{" "}
              <Link
                onClick={(e) => {
                  e.preventDefault();
                  navigate(ROUTE.SETTINGS_APPLICATION_TYPES);
                }}
                href={ROUTE.SETTINGS_APPLICATION_TYPES}
              >
                Application types
              </Link>{" "}
              or{" "}
              <Link
                onClick={(e) => {
                  e.preventDefault();
                  navigate(ROUTE.SETTINGS_BRANDING);
                }}
                href={ROUTE.SETTINGS_BRANDING}
              >
                Branding
              </Link>
              .
            </Paragraph>
          )}

          <Button
            type="submit"
            size="large"
            width="100%"
            mt="4"
            mb="0"
            isDisabled={
              !formikProps.dirty ||
              (formikProps.dirty && !formikProps.isValid) ||
              isLoadingUpdate ||
              !hasPermission("divisions.manage")
            }
            title={
              hasPermission("divisions.manage")
                ? undefined
                : "You don't have permission to manage divisions"
            }
          >
            {mode === "edit" ? "Save" : "Next"}
          </Button>
        </Form>
      )}
    </Formik>
  );
};

const TeamsTab = ({ division }: { division: Division }) => {
  const { hasPermission } = useAuth();
  const { data: teams, isLoading } = useTeams(division.id);
  const { mutate: deleteTeam } = useDeleteTeam();
  const [pendingDeleteTeam, setPendingDeleteTeam] = useState<Team | null>(
    null
  );

  const teamList = teams?.data ?? [];

  const confirmDeleteTeam = () => {
    if (!pendingDeleteTeam) return;
    deleteTeam(
      { id: pendingDeleteTeam.id },
      {
        onSuccess: () => {
          notify("Team deleted", { type: "success" });
          queryClient.invalidateQueries(["teams"]);
        },
        onError: (err: any) =>
          showError(
            err,
            "This Team still has members or Mandates linked to it — reassign those first."
          ),
      }
    );
    setPendingDeleteTeam(null);
  };

  if (isLoading) {
    return (
      <Flex justifyContent="center" p={4}>
        <Loader />
      </Flex>
    );
  }

  if (teamList.length === 0) {
    return (
      <Paragraph color="gray.60" mt={3}>
        No Teams in this Division yet.
      </Paragraph>
    );
  }

  return (
    <Box mt={3}>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        py={1}
        borderBottom="1px solid"
        borderBottomColor="gray.30"
      >
        <Paragraph m="0px" color="gray.50">
          Team Name
        </Paragraph>
      </Flex>
      {teamList.map((team) => {
        const deleteBlockedReason = teamDeleteBlockedReason(
          team,
          hasPermission("teams.manage")
        );

        return (
          <Flex
            key={team.id}
            justifyContent="space-between"
            alignItems="center"
            py={2}
            borderBottom="1px solid"
            borderBottomColor="gray.10"
          >
            <Paragraph m="0px">{team.display_name}</Paragraph>
            {team.can_delete && !team.is_default && (
              <Image
                src={deleteIcon}
                alt="delete team"
                height="20px"
                width="20px"
                style={{
                  cursor: deleteBlockedReason ? "not-allowed" : "pointer",
                  opacity: deleteBlockedReason ? 0.4 : 1,
                }}
                title={deleteBlockedReason}
                onClick={() =>
                  !deleteBlockedReason && setPendingDeleteTeam(team)
                }
              />
            )}
          </Flex>
        );
      })}

      <ConfirmModal
        isOpen={Boolean(pendingDeleteTeam)}
        onRequestClose={() => setPendingDeleteTeam(null)}
        handleConfirm={confirmDeleteTeam}
        title={
          pendingDeleteTeam
            ? `Delete "${pendingDeleteTeam.display_name}"?`
            : undefined
        }
        content="This permanently removes the Team and can't be undone."
      />
    </Box>
  );
};

/** Drafts one or more Teams locally while the parent Division doesn't exist yet — nothing
 * here is saved until the whole form's final "Create Division" call fires. */
const NewDivisionTeamsTab = ({
  divisionName,
  teamDrafts,
  onAddTeam,
  onRemoveTeam,
  onCreateAll,
  isSaving,
}: {
  divisionName?: string;
  teamDrafts: TeamDraft[];
  onAddTeam: (team: TeamDraft) => void;
  onRemoveTeam: (index: number) => void;
  onCreateAll: () => void;
  isSaving: boolean;
}) => {
  const { hasPermission } = useAuth();

  return (
    <Box mt={3}>
      <Paragraph color="gray.50" fontSize={1} mb={3}>
        Add at least one Team for {divisionName || "this Division"} — more can
        always be added later from the Division's own Teams tab.
      </Paragraph>

      <Formik
        initialValues={{ name: "", display_name: "", is_active: true }}
        validationSchema={teamDraftValidation}
        onSubmit={(values, { resetForm }) => {
          onAddTeam({
            name: values.name.trim(),
            display_name: values.display_name.trim(),
            is_active: values.is_active,
          });
          resetForm();
        }}
      >
        {(formikProps) => (
          <Form onSubmit={formikProps.handleSubmit}>
            <Input
              onChange={formikProps.handleChange}
              value={formikProps.values.name}
              placeholder="Enter team name"
              mb={2}
              label="Name"
              name="name"
              hasError={Boolean(formikProps.errors.name)}
              errorMessage={formikProps.errors.name as string}
            />
            <Input
              onChange={formikProps.handleChange}
              value={formikProps.values.display_name}
              placeholder="Enter display name"
              mb={2}
              label="Display name"
              name="display_name"
              hasError={Boolean(formikProps.errors.display_name)}
              errorMessage={formikProps.errors.display_name as string}
            />
            <Flex justifyContent="flex-end" mb={3}>
              <Button
                type="submit"
                variant="secondary"
                size="small"
                isDisabled={!hasPermission("teams.manage")}
                title={
                  hasPermission("teams.manage")
                    ? undefined
                    : "You don't have permission to manage teams"
                }
              >
                Add Team
              </Button>
            </Flex>
          </Form>
        )}
      </Formik>

      {teamDrafts.length > 0 && (
        <Box mb={3}>
          {teamDrafts.map((team, index) => (
            <Flex
              key={`${team.name}-${index}`}
              justifyContent="space-between"
              alignItems="center"
              py={2}
              borderBottom="1px solid"
              borderBottomColor="gray.10"
            >
              <Paragraph m="0px">{team.display_name}</Paragraph>
              <Image
                src={deleteIcon}
                alt="remove team draft"
                height="20px"
                width="20px"
                style={{
                  cursor: hasPermission("teams.manage")
                    ? "pointer"
                    : "not-allowed",
                  opacity: hasPermission("teams.manage") ? 1 : 0.4,
                }}
                title={
                  hasPermission("teams.manage")
                    ? undefined
                    : "You don't have permission to manage teams"
                }
                onClick={() =>
                  hasPermission("teams.manage") && onRemoveTeam(index)
                }
              />
            </Flex>
          ))}
        </Box>
      )}

      <Button
        size="large"
        width="100%"
        mt="2"
        mb="0"
        onClick={onCreateAll}
        isDisabled={
          isSaving ||
          teamDrafts.length === 0 ||
          !hasPermission("divisions.manage") ||
          !hasPermission("teams.manage")
        }
        title={
          !hasPermission("divisions.manage")
            ? "You don't have permission to manage divisions"
            : !hasPermission("teams.manage")
            ? "You don't have permission to manage teams"
            : undefined
        }
      >
        {isSaving ? "Creating..." : "Create Division"}
      </Button>
      {teamDrafts.length === 0 && (
        <Paragraph color="gray.50" fontSize={1} mt={2} textAlign="center">
          Add at least one Team to create this Division.
        </Paragraph>
      )}
    </Box>
  );
};

const MembersTab = ({ division }: { division: Division }) => {
  const { hasPermission } = useAuth();
  const [search, setSearch] = useState("");
  const [pendingToggle, setPendingToggle] = useState<
    { user: User; isMember: boolean } | null
  >(null);
  // Unfiltered: every tenant user, to toggle direct Division membership against.
  const { data: allUsers, isLoading: isLoadingAll } = useUsers();
  // Filtered: who's already a direct member of this Division.
  const { data: divisionUsers, isLoading: isLoadingDivision } = useUsers({
    division: division.id,
  });
  const { mutate: updateUser } = useUpdateUserDetails();

  const isLoading = isLoadingAll || isLoadingDivision;
  const memberIds = new Set(
    (divisionUsers?.data?.results ?? []).map(({ id }) => id)
  );

  const users = (allUsers?.data?.results ?? []).filter((user) =>
    `${user.first_name} ${user.last_name} ${user.email}`
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  const toggleMember = (user: User, isMember: boolean) => {
    const currentDivisionIds = (user.division ?? []).map(({ id }) => id);
    const currentActiveDivisionIds = (user.active_division ?? []).map(
      ({ id }) => id
    );

    const nextDivisionIds = isMember
      ? Array.from(new Set([...currentDivisionIds, division.id]))
      : currentDivisionIds.filter((id) => id !== division.id);
    const nextActiveDivisionIds = isMember
      ? Array.from(new Set([...currentActiveDivisionIds, division.id]))
      : currentActiveDivisionIds.filter((id) => id !== division.id);

    updateUser(
      {
        id: user.id,
        division: nextDivisionIds,
        active_division: nextActiveDivisionIds,
      },
      {
        onSuccess: () => {
          notify(
            `${user.first_name} ${
              isMember ? "added to" : "removed from"
            } ${division.display_name}`,
            { type: "success" }
          );
          queryClient.invalidateQueries(["users"]);
        },
        onError: (err: any) =>
          showError(
            err,
            "A problem occurred while updating Division membership."
          ),
      }
    );
  };

  if (isLoading) {
    return (
      <Flex justifyContent="center" p={4}>
        <Loader />
      </Flex>
    );
  }

  return (
    <Box mt={3}>
      <Paragraph color="gray.50" fontSize={1} mb={2}>
        Direct Division membership — separate from being a member of one of
        its Teams. Used for people who need visibility across the whole
        Division without belonging to a specific Team.
      </Paragraph>
      <Flex justifyContent="space-between" alignItems="center" mb={2}>
        <Input
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          type="search"
          size="small"
          placeholder="Search"
          maxWidth={200}
        />
        <Paragraph color="gray.50" m="0px">
          {memberIds.size} Member{memberIds.size === 1 ? "" : "s"}
        </Paragraph>
      </Flex>

      {users.length === 0 && (
        <Paragraph color="gray.60" mt={3}>
          No users found.
        </Paragraph>
      )}

      {users.map((user) => (
        <Flex
          key={user.id}
          justifyContent="space-between"
          alignItems="center"
          py={2}
          borderBottom="1px solid"
          borderBottomColor="gray.10"
        >
          <Flex alignItems="center" gap="8">
            <ProfileOrb
              firstName={user.first_name}
              lastName={user.last_name}
              colour={user.colour}
            />
            <Box>
              <Paragraph m="0px" fontWeight="bold">
                {user.first_name} {user.last_name}
              </Paragraph>
              <Paragraph m="0px" color="gray.50" fontSize={1}>
                {user.email}
              </Paragraph>
            </Box>
          </Flex>
          <Toggle
            isChecked={memberIds.has(user.id)}
            isDisabled={!hasPermission("assign.division")}
            title={
              hasPermission("assign.division")
                ? undefined
                : "You don't have permission to assign division membership"
            }
            onChange={(e) =>
              setPendingToggle({ user, isMember: e.target.checked })
            }
          />
        </Flex>
      ))}

      <ConfirmModal
        isOpen={Boolean(pendingToggle)}
        onRequestClose={() => setPendingToggle(null)}
        handleConfirm={() => {
          if (pendingToggle) {
            toggleMember(pendingToggle.user, pendingToggle.isMember);
          }
          setPendingToggle(null);
        }}
        title={
          pendingToggle
            ? `${pendingToggle.isMember ? "Add" : "Remove"} ${
                pendingToggle.user.first_name
              } ${pendingToggle.isMember ? "to" : "from"} ${
                division.display_name
              }?`
            : undefined
        }
        content={
          pendingToggle?.isMember
            ? `${pendingToggle.user.first_name} will immediately be able to see everything tagged to this Division.`
            : pendingToggle
            ? `${pendingToggle.user.first_name} will immediately lose the visibility this Division membership gave them.`
            : undefined
        }
      />
    </Box>
  );
};

export const AddEditDivisionModal = (props: AddEditDivisionModalProps) => {
  const { isOpen, onRequestClose, division } = props;
  const mode = division ? "edit" : "create";

  const [activeTab, setActiveTab] = useState(0);
  const [divisionDraft, setDivisionDraft] = useState<DivisionDraft | null>(
    null
  );
  const [teamDrafts, setTeamDrafts] = useState<TeamDraft[]>([]);
  const [isSavingAll, setIsSavingAll] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setActiveTab(0);
      setDivisionDraft(null);
      setTeamDrafts([]);
      setIsSavingAll(false);
    }
  }, [isOpen, division]);

  const { mutate: createDivision } = useCreateDivision();
  const { mutate: createTeamsBulk } = useCreateTeamsBulk();

  // Single completion path — every branch below (success, partial failure,
  // unexpected shape) MUST end here so the slider can never get stuck open
  // with the button frozen on "Creating...".
  const finish = (message: string, type: "success" | "error") => {
    notify(message, { type });
    queryClient.invalidateQueries(["divisions"]);
    queryClient.invalidateQueries(["teams"]);
    setIsSavingAll(false);
    onRequestClose();
  };

  const handleCreateDivisionAndTeams = () => {
    if (!divisionDraft || teamDrafts.length === 0) return;
    setIsSavingAll(true);

    createDivision(divisionDraft, {
      onSuccess: (res: any) => {
        const newDivisionId = res?.data?.id;

        if (!newDivisionId) {
          // The Division was created, but its id wasn't in the response, so
          // the drafted Teams can't be attached to it here.
          showError(
            res,
            "The Division was created, but its Teams couldn't be added automatically — add them from the Division's own Teams tab."
          );
          finish("Division created", "success");
          return;
        }

        createTeamsBulk(
          teamDrafts.map((team) => ({ ...team, division: newDivisionId })),
          {
            onSuccess: () => finish("Division and Teams created", "success"),
            onError: (err: any) => {
              showError(
                err,
                "The Division was created, but a problem occurred while creating its Teams — add them from the Division's own Teams tab."
              );
              finish(
                "Division created — Teams could not be created.",
                "error"
              );
            },
          }
        );
      },
      onError: (err: any) => {
        setIsSavingAll(false);
        showError(err, "A problem occurred while creating the Division.");
      },
    });
  };

  const createTabs = [
    {
      label: "Details",
      content: (
        <DetailsTab
          division={division}
          onSaved={onRequestClose}
          onNext={(draft) => {
            setDivisionDraft(draft);
            setActiveTab(1);
          }}
        />
      ),
    },
    {
      label: "Teams",
      content: divisionDraft ? (
        <NewDivisionTeamsTab
          divisionName={divisionDraft.display_name}
          teamDrafts={teamDrafts}
          onAddTeam={(team) => setTeamDrafts((prev) => [...prev, team])}
          onRemoveTeam={(index) =>
            setTeamDrafts((prev) => prev.filter((_, i) => i !== index))
          }
          onCreateAll={handleCreateDivisionAndTeams}
          isSaving={isSavingAll}
        />
      ) : (
        <Paragraph color="gray.60" mt={3}>
          Complete the Details tab first.
        </Paragraph>
      ),
    },
  ];

  const editTabs = [
    {
      label: "Details",
      content: (
        <DetailsTab
          division={division}
          onSaved={onRequestClose}
          onNext={() => {}}
        />
      ),
    },
    {
      label: "Teams",
      content: division ? (
        <TeamsTab division={division} />
      ) : (
        <Paragraph color="gray.60" mt={3}>
          Save the Division first, then come back here to manage its Teams.
        </Paragraph>
      ),
    },
    {
      label: "Members",
      content: division ? (
        <MembersTab division={division} />
      ) : (
        <Paragraph color="gray.60" mt={3}>
          Save the Division first, then come back here to manage its direct
          Members.
        </Paragraph>
      ),
    },
  ];

  return (
    <Slider isOpen={isOpen} onRequestClose={onRequestClose} width="450px">
      <Box px={4} py={3} height="94%" overflowY="auto">
        <Paragraph fontWeight="bold" fontSize={3}>
          {mode === "edit" ? "Edit Division" : "New Division"}
        </Paragraph>
        <Box borderBottom="1px solid" borderBottomColor="gray.30" mb={3} />

        <CustomTabs
          showDividerBelowNav
          activeTab={mode === "create" ? activeTab : undefined}
          onTabChange={mode === "create" ? setActiveTab : undefined}
          tabs={mode === "create" ? createTabs : editTabs}
        />
      </Box>
    </Slider>
  );
};
